"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.entryWorklistPageSchema = exports.entryWorklistItemSchema = exports.entryWorklistQuerySchema = exports.WORKLIST_WAITS = void 0;
/**
 * `RPM-004` — the worklist for confirming contact, as `CR-44` states it (`J-531`).
 *
 * An entry is everything entered on Enter vitals for one resident at one date and time, and its
 * current completion record is the newest `rpm_alerts` row no correction has superseded. The
 * worklist is a query over those records: the entries owing a contact not yet confirmed
 * (obligation `OWED`), and those whose contact cannot be made (`UNMET`) until they are completed.
 * `notFinished` asks the other list `RPM-089` names: every entry whose current record is neither
 * `CLOSED` nor `SUPERSEDED`.
 *
 * NO NAME AND NO RESIDENT ID TRAVELS IN THE QUERY (`.claude/rules/phi.md` rule 2; `logging.md`: the
 * HTTP instrumentation records `url.query` on spans). The block's Resident filter matches part of a
 * name; the screen applies it in the browser over the rows it loaded, as the resident picker matches
 * names over the roster it holds. Only building and care-unit ids, which are configuration, travel.
 */
const zod_1 = require("zod");
const rpm_enums_js_1 = require("./db/rpm-enums.js");
const rpm_alert_routing_js_1 = require("./rpm-alert-routing.js");
/** The mirror's identifier width. `carease.facilities.id` is varchar(20), and no FK reaches it. */
const MIRROR_ID = zod_1.z.string().min(1).max(20);
/** A comma-separated list in one query parameter, each member validated by `member`. */
const csv = (member) => zod_1.z
    .string()
    .min(1)
    .max(2000)
    .transform((s) => s.split(",").map((x) => x.trim()))
    .pipe(zod_1.z.array(member).min(1).max(100));
/** The waits the Waiting longer than control offers, in minutes. */
exports.WORKLIST_WAITS = { "30m": 30, "1h": 60, "4h": 240 };
exports.entryWorklistQuerySchema = zod_1.z
    .strictObject({
    /** Without one, every building the account can reach. */
    facilityId: MIRROR_ID.optional(),
    /** `RPM-089`'s list: entries not yet completed, whatever they owe. */
    notFinished: zod_1.z.stringbool().default(false),
    enteredBy: zod_1.z.enum(["me", "anyone"]).default("anyone"),
    /** One or more care units, within the chosen building. */
    careUnitIds: csv(zod_1.z.uuid()).optional(),
    lane: zod_1.z.enum(rpm_enums_js_1.alertLane.enumValues).optional(),
    contactMethod: zod_1.z.enum(["CALL", "EMAIL", "TEXT", "DAILY_SUMMARY"]).optional(),
    /** The taken date range, both ends inclusive, as the entry's own local date. */
    takenFrom: zod_1.z.iso.date().optional(),
    takenTo: zod_1.z.iso.date().optional(),
    waitingLongerThan: zod_1.z.enum(["30m", "1h", "4h"]).optional(),
    /** `0` reads the counts alone: Not finished's collapsed line lists no entry (`CR-45`). */
    limit: zod_1.z.coerce.number().int().min(0).max(200).default(50),
    /** Opaque. The server issues it and the server reads it. */
    cursor: zod_1.z.string().min(1).max(200).optional(),
})
    .refine((q) => q.careUnitIds === undefined || q.facilityId !== undefined, {
    message: "A care unit filter needs a building.",
    path: ["careUnitIds"],
})
    .refine((q) => q.takenFrom === undefined || q.takenTo === undefined || q.takenFrom <= q.takenTo, {
    message: "takenFrom is after takenTo.",
    path: ["takenFrom"],
})
    .meta({ description: "The entries waiting for a contact to be confirmed, or not finished" });
const entryReadingSchema = zod_1.z.object({
    vitalReadingId: zod_1.z.uuid(),
    vitalCode: zod_1.z.enum(rpm_enums_js_1.vitalCode.enumValues),
    valueText: zod_1.z.string(),
    severityBand: zod_1.z.enum(rpm_enums_js_1.severityBand.enumValues).nullable(),
});
/** One contact still to reach: a step of the routing that is owed, waiting or cannot be made. */
const stillToReachSchema = zod_1.z.object({
    order: rpm_alert_routing_js_1.routingStepSchema.shape.order,
    method: rpm_alert_routing_js_1.routingStepSchema.shape.method,
    state: rpm_alert_routing_js_1.routingStepSchema.shape.state,
    /** What a waiting step waits for. */
    due: rpm_alert_routing_js_1.routingStepSchema.shape.due,
    recipients: zod_1.z.array(rpm_alert_routing_js_1.routingRecipientSchema),
    /** Why the step cannot be made, from `RPM-035`'s closed list. */
    reason: zod_1.z.string().nullable(),
    /** The outcomes logged against the step so far: on a step still to reach, none of them ended it. */
    logged: rpm_alert_routing_js_1.routingStepSchema.shape.logged,
});
exports.entryWorklistItemSchema = zod_1.z.object({
    /** The entry's current completion record. Opening the row opens this entry on Enter vitals. */
    rpmAlertId: zod_1.z.uuid(),
    facilityId: MIRROR_ID,
    facilityName: zod_1.z.string().nullable(),
    careasePatientId: MIRROR_ID,
    residentName: zod_1.z.string().nullable(),
    /** The resident's unit and room as the roster holds them now. */
    unit: zod_1.z.string().nullable(),
    room: zod_1.z.string().nullable(),
    careUnitId: zod_1.z.uuid().nullable(),
    careUnitLabel: zod_1.z.string().nullable(),
    takenAtLocalDate: zod_1.z.string(),
    takenAtLocalTime: zod_1.z.string(),
    /** The entry's current readings: a corrected reading is replaced by the one that corrects it. */
    readings: zod_1.z.array(entryReadingSchema),
    lane: zod_1.z.enum(rpm_enums_js_1.alertLane.enumValues).nullable(),
    laneOverridden: zod_1.z.boolean(),
    alertState: zod_1.z.enum(rpm_enums_js_1.alertState.enumValues),
    obligation: zod_1.z.enum(rpm_enums_js_1.obligationStatus.enumValues),
    unmetReason: zod_1.z.string().nullable(),
    stillToReach: zod_1.z.array(stillToReachSchema),
    attempts: zod_1.z.object({
        count: zod_1.z.number().int(),
        lastOutcome: zod_1.z.enum(rpm_enums_js_1.attemptOutcome.enumValues).nullable(),
        lastAt: zod_1.z.iso.datetime({ offset: true }).nullable(),
    }),
    /**
     * The call this entry's contact is held for (`RPM-039`, `CR-44`): its batch, still open, with when
     * it is due and how many residents it covers. Null where the entry is on no open batch.
     */
    heldCall: zod_1.z
        .object({
        heldCallId: zod_1.z.uuid(),
        batchState: zod_1.z.enum(["PENDING", "ATTEMPTING"]),
        dueAt: zod_1.z.iso.datetime({ offset: true }),
        destinationLabel: zod_1.z.string().nullable(),
        destinationValue: zod_1.z.string(),
        residents: zod_1.z.number().int(),
    })
        .nullable(),
    /**
     * When the daily summary this entry waits for falls due (`RPM-103`, `CR-44`): an entry whose
     * summary step is still waiting is shown with it. Null where no summary step waits.
     */
    summaryDueAt: zod_1.z.iso.datetime({ offset: true }).nullable(),
    /** When the entry's current record opened: the longest wait sorts first after criticals. */
    waitingSince: zod_1.z.iso.datetime({ offset: true }),
    enteredBy: zod_1.z.object({ userId: zod_1.z.string(), name: zod_1.z.string().nullable() }),
    /**
     * What is left before Complete entry, one sentence each, in the words the entry itself shows
     * (`RPM-089`, `RPM-090`, `CR-45`): its one list of blocking reasons, then each step owed now, then
     * the call held for its batch — the sentences Complete entry's refusal names. Empty where nothing is
     * left. Present on Not finished only; the worklist does not ask for it.
     */
    whatIsLeft: zod_1.z.array(zod_1.z.string()).optional(),
});
exports.entryWorklistPageSchema = zod_1.z
    .object({
    items: zod_1.z.array(exports.entryWorklistItemSchema),
    nextCursor: zod_1.z.string().nullable(),
    /** Every entry the filters match, across pages, in the buildings the account can reach. */
    total: zod_1.z.number().int(),
    /**
     * Not finished's collapsed line (`RPM-089`, `CR-45`): the entries the signed-in account entered,
     * how many of those are critical, and everyone's, in the buildings asked for. Present on Not
     * finished only.
     */
    counts: zod_1.z
        .object({
        yours: zod_1.z.number().int(),
        yoursCritical: zod_1.z.number().int(),
        everyone: zod_1.z.number().int(),
    })
        .optional(),
})
    .meta({ description: "One page of the worklist" });
//# sourceMappingURL=rpm-entry-worklist.js.map